import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Shield, Plus, Search, Filter, RefreshCw, Eye, Settings,
  CheckCircle2, Clock, XCircle, Loader2, Users, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReleaseStrategyList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('strategies');
  
  const [strategies, setStrategies] = useState([]);
  const [releaseCodes, setReleaseCodes] = useState([]);
  const [releaseGroups, setReleaseGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [strategiesRes, codesRes, groupsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/release/strategies`),
        axios.get(`${BACKEND_URL}/api/release/codes`),
        axios.get(`${BACKEND_URL}/api/release/groups`)
      ]);
      setStrategies(strategiesRes.data);
      setReleaseCodes(codesRes.data);
      setReleaseGroups(groupsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load release strategy data');
    } finally {
      setLoading(false);
    }
  };

  const getObjectTypeBadge = (type) => {
    const colors = {
      'PR': 'bg-orange-100 text-orange-800',
      'PO': 'bg-green-100 text-green-800',
      'SO': 'bg-orange-100 text-orange-800',
      'RFQ': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const filteredStrategies = strategies.filter(s =>
    s.strategy_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCodes = releaseCodes.filter(c =>
    c.release_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = releaseGroups.filter(g =>
    g.group_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="release-strategy-list">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-orange-600" />
              Release Strategy
            </h1>
            <p className="text-sm text-gray-500">Configure approval workflows for documents</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/release/inbox')}>
              <FileText className="w-4 h-4 mr-2" />
              Approval Inbox
            </Button>
            {canWrite('release_strategy') && (
            <Button onClick={() => navigate('/release/strategies/create')} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              New Strategy
            </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          {[
            { id: 'strategies', label: 'Strategies', count: strategies.length },
            { id: 'codes', label: 'Release Codes', count: releaseCodes.length },
            { id: 'groups', label: 'Approval Groups', count: releaseGroups.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-orange-600 text-orange-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : (
          <>
            {/* Strategies Tab */}
            {activeTab === 'strategies' && (
              <div className="bg-white rounded-lg shadow-sm border">
                {filteredStrategies.length === 0 ? (
                  <div className="p-8 text-center">
                    <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-600">No release strategies configured</p>
                    {canWrite('release_strategy') && (
                    <Button onClick={() => navigate('/release/strategies/create')} className="mt-4" variant="outline">
                      Create Strategy
                    </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Strategy ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Object Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Release Codes</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Priority</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredStrategies.map((strategy) => (
                          <tr key={strategy.strategy_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-orange-600">{strategy.strategy_id}</td>
                            <td className="px-4 py-3 text-gray-900">{strategy.description}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getObjectTypeBadge(strategy.object_type)}`}>
                                {strategy.object_type}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 flex-wrap">
                                {strategy.release_codes?.map((code, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{code}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">{strategy.priority}</td>
                            <td className="px-4 py-3 text-center">
                              {strategy.is_active ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400 mx-auto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Release Codes Tab */}
            {activeTab === 'codes' && (
              <div className="bg-white rounded-lg shadow-sm border">
                {filteredCodes.length === 0 ? (
                  <div className="p-8 text-center">
                    <Settings className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-600">No release codes configured</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approvers</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Sequence</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredCodes.map((code) => (
                          <tr key={code.release_code} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-orange-600">{code.release_code}</td>
                            <td className="px-4 py-3 text-gray-900">{code.description}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 flex-wrap">
                                {code.approver_users?.map((user, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">{user}</span>
                                ))}
                                {code.approver_groups?.map((group, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">{group}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">{code.sequence}</td>
                            <td className="px-4 py-3 text-center">
                              {code.is_mandatory ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <span className="text-gray-400">-</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {code.is_active ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400 mx-auto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Approval Groups Tab */}
            {activeTab === 'groups' && (
              <div className="bg-white rounded-lg shadow-sm border">
                {filteredGroups.length === 0 ? (
                  <div className="p-8 text-center">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-600">No approval groups configured</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Members</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Min Approvals</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredGroups.map((group) => (
                          <tr key={group.group_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-orange-600">{group.group_id}</td>
                            <td className="px-4 py-3 text-gray-900">{group.description}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 flex-wrap">
                                {group.members?.map((member, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{member}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">{group.minimum_approvals}</td>
                            <td className="px-4 py-3 text-center">
                              {group.is_active ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400 mx-auto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ReleaseStrategyList;
